import { AppDispatch, setPage, setRowsPerPage } from 'data';
import { selectedSearchQuery } from 'data/patient-management/selector';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { colors, ICON_TYPE, TIME_RANGE } from '../../constants';
import { getTimeRangeValue, iconHandler } from '../../utils';
import { SelectChangeEvent } from '@mui/material';
import { useS3Uploader, useTableData } from '..';
import { ConnectedUsers } from '../../pages/DoctorManagement/types';
import { dateFormat, getTimestamps } from '../../helper';
import { useAppointmentTrends } from './useAppointmentTrends';
import { useVerificationTrends } from './useVerificationTrends';
import { useRegistrationTrends } from './useRegistrationTrends';
import { usePatientStats } from './usePatientStats';
import moment from 'moment';
import { Patient, usePatientsListQuery } from './usePatientsListQuery';

export interface PatientManagementRow {
  id: string;
  sNo: number;
  fullName: string;
  email: string;
  status: string;
  gender: string;
  image: string;
  connectedUsers: ConnectedUsers[];
  DOB?: string;
  verifiedOn?: string;
}

const usePatientManagement = (rowsPerPage: number = 10, page: number = 0) => {
  const dispatch = useDispatch<AppDispatch>();
  const { generateSignedUrl } = useS3Uploader();
  const searchQuery = useSelector(selectedSearchQuery);

  /* -------------------- UI STATE -------------------- */
  const [selectedValue, setSelectedValue] = useState({
    registrationRange: TIME_RANGE.YEARLY,
    verificationRange: TIME_RANGE.YEARLY,
    appointmentRange: TIME_RANGE.YEARLY
  });

  const [currentFilters, setCurrentFilters] = useState<{
    gender?: string;
    ageStart?: string;
    ageEnd?: string;
  }>({});

  /* -------------------- TABLE STATE -------------------- */
  const {
    tableRows,
    setTableRows,
    hasMore,
    setHasMore,
    totalCount,
    setTotalCount,
    pageTimestamps,
    setPageTimestamps
  } = useTableData<PatientManagementRow>();

  const ArrowDownIcon = () => iconHandler(ICON_TYPE.ARROW_DOWN, colors.black, 1.2, 30);

  /* -------------------- TABLE ROW MAPPER -------------------- */
  const TableRows = async (patients: Patient[]) => {
    return Promise.all(
      patients.map(async (item, index) => {
        const patientImageUrl = item.image ? await generateSignedUrl(item.image) : '';

        const connectedUsers = await Promise.all(
          (item.connectedUsers || []).map(async (doc: ConnectedUsers) => ({
            name: doc.name,
            image: doc.image ? await generateSignedUrl(doc.image) : ''
          }))
        );

        return {
          ...item,
          id: item.id,
          sNo: page * rowsPerPage + index + 1,
          fullName: item.fullName,
          gender: item.gender || '',
          email: item.email || 'N/A',
          status: item.status || '',
          image: patientImageUrl,
          connectedUsers
        };
      })
    );
  };

  /* -------------------- HANDLERS -------------------- */
  const handleChange = (event: SelectChangeEvent<string>) => {
    setSelectedValue((prev) => ({
      ...prev,
      [event.target.name]: event.target.value
    }));
  };

  const updateFilters = (filters: { gender?: string; ageStart?: string; ageEnd?: string }) => {
    setCurrentFilters(filters);
  };

  /* -------------------- TABLE API -------------------- */
  const currentPageTimestamp = page === 0 ? undefined : pageTimestamps.get(page - 1);

  const { data: result, isLoading: tableLoading } = usePatientsListQuery({
    limit: rowsPerPage,
    created_at: currentPageTimestamp,
    gender: currentFilters.gender,
    ageStart: currentFilters.ageStart,
    ageEnd: currentFilters.ageEnd
  });

  useEffect(() => {
    if (!result?.data) {
      setTableRows([]);
      return;
    }

    (async () => {
      const rows = await TableRows(result.data);
      setTableRows(rows);
      setHasMore(result.hasMore ?? false);
      setTotalCount(result.totalCount ?? 0);

      if (result.lastUpdatedAt) {
        setPageTimestamps((prev) => {
          const map = new Map(prev);
          map.set(page, result.lastUpdatedAt || '');
          return map;
        });
      }
    })();
  }, [result, page]);

  /* -------------------- SEARCH FILTER (TABLE ONLY) -------------------- */
  const filteredTableRows = useMemo(() => {
    if (!searchQuery) return tableRows;

    const q = searchQuery.toLowerCase();
    return tableRows.filter(
      (item) => item.fullName?.toLowerCase().includes(q) || item.email?.toLowerCase().includes(q)
    );
  }, [tableRows, searchQuery]);

  /* -------------------- RESET PAGINATION -------------------- */
  useEffect(() => {
    dispatch(setPage(0));
    dispatch(setRowsPerPage(10));
  }, [dispatch]);

  /* ==================== STATS (LOAD ONCE) ==================== */
  const statsRange = useMemo(() => {
    const now = moment();
    return {
      startTimestamp: now.format(dateFormat.TIME_STAMP),
      endTimestamp: now.add(1, 'month').format(dateFormat.TIME_STAMP)
    };
  }, []);

  const { patientStats, isLoading: statsLoading } = usePatientStats(statsRange);

  /* ==================== APPOINTMENT CHART ==================== */
  const appointmentTimestamps = useMemo(
    () => getTimestamps(selectedValue.appointmentRange),
    [selectedValue.appointmentRange]
  );

  const appointmentPayload = useMemo(
    () => ({
      startTimestamp: appointmentTimestamps.startTimestamp,
      endTimestamp: appointmentTimestamps.endTimestamp
    }),
    [appointmentTimestamps]
  );

  const { chartData: appointmentTrends } = useAppointmentTrends(appointmentPayload);

  /* ==================== VERIFICATION CHART ==================== */
  const verificationPayload = useMemo(
    () => ({
      rangeType: getTimeRangeValue(selectedValue.verificationRange)
    }),
    [selectedValue.verificationRange]
  );

  const { chartData: verificationTrends } = useVerificationTrends(
    verificationPayload,
    selectedValue.verificationRange
  );

  /* ==================== REGISTRATION CHART ==================== */
  const registrationPayload = useMemo(
    () => ({
      rangeType: getTimeRangeValue(selectedValue.registrationRange)
    }),
    [selectedValue.registrationRange]
  );

  const { chartData: registrationTrends } = useRegistrationTrends(
    registrationPayload,
    selectedValue.registrationRange
  );

  /* ==================== RETURN ==================== */
  return {
    loading: statsLoading,
    error: false,
    tableLoading,
    patientStats,
    appointmentTrends,
    verificationTrends,
    registrationTrends,
    selectedValue,
    handleChange,
    tableRows: filteredTableRows,
    ArrowDownIcon,
    searchQuery,
    hasMore,
    totalCount,
    updateFilters
  };
};

export default usePatientManagement;
